import { useState, useEffect, useMemo, useRef } from 'react';
import Card from '../components/common/Card.jsx';
import Table, { UtilBar } from '../components/common/Table.jsx';
import MultiSelect from '../components/common/MultiSelect.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import LineChart from '../components/charts/LineChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import GroupedBarChart from '../components/charts/GroupedBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell } from '../components/optimization/RecCells.jsx';
import {
  parseCost, parseSaving, formatSavingStr,
  buildBaseCostKpis, exportCSV,
  getScopedItem, setScopedItem, STORAGE_KEYS,
} from '../components/common/Helper';

import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import APIConstants from '../services/APIConstants';
import { useAuth } from '../state/AuthContext';

// ── Tabs ─────────────────────────────────────────────────────

const subTabs = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

const VIEW_TABS = [
  { key: 'sqlDatabase',         label: 'SQL Database' },
  { key: 'sqlElasticPool',      label: 'SQL Elastic Pool' },
  { key: 'managedInstance',     label: 'Managed Instance' },
  { key: 'managedInstancePool', label: 'Managed Instance Pool' },
];

// Per-view-tab API type strings + display metadata. Each view has its own Combined /
// Custom (CloudsAnalytics) / Azure recommendation endpoints plus the filter-tag type.
// `kind` drives which backend keys the row mapper reads (database vs pool). Add entries
// here as the backend APIs for the other resource types become available.
const VIEW_API_TYPES = {
  sqlDatabase: {
    combined: 'COMBINED_SQL_DATABASE',
    custom:   'CUSTOM_SQL_DATABASE',
    azure:    'SQL_DATABASE',
    tagType:  'SQL_DATABASE',
    kind:     'database',
    entity:   'Database',
    entityPlural: 'databases',
    kpiTitle: 'SQL Databases',
  },
  sqlElasticPool: {
    combined: 'COMBINED_ELASTIC_POOL_DATABASE',
    custom:   'CUSTOM_ELASTIC_POOL_DATABASE',
    azure:    'ELASTIC_POOL_DATABASE',
    tagType:  'ELASTIC_POOL_DATABASE',
    kind:     'pool',
    entity:   'Pool',
    entityPlural: 'pools',
    kpiTitle: 'Elastic Pools',
  },
  managedInstance: {
    combined: 'COMBINED_MANAGED_INSTANCE_DATABASE',
    custom:   'CUSTOM_MANAGED_INSTANCE_DATABASE',
    azure:    'MANAGED_INSTANCE_DATABASE',
    tagType:  'MANAGED_INSTANCE_DATABASE',
    kind:     'managedInstance',
    entity:   'Managed Instance',
    entityPlural: 'managed instances',
    kpiTitle: 'Managed Instances',
  },
  managedInstancePool: {
    combined: 'COMBINED_MANAGED_INSTANCE_POOL_DATABASE',
    custom:   'CUSTOM_MANAGED_INSTANCE_POOL_DATABASE',
    azure:    'MANAGED_INSTANCE_POOL_DATABASE',
    tagType:  'MANAGED_INSTANCE_POOL_DATABASE',
    kind:     'pool',
    entity:   'Managed Instance Pool',
    entityPlural: 'managed instance pools',
    kpiTitle: 'Managed Instance Pools',
  },
};

// ── Rec helpers ──────────────────────────────────────────────

const resolveSQLRec = (recType) => {
  const t = (recType ?? '').toUpperCase().trim();
  let recPillClass = 'rec-ok';
  if (['STOP', 'DELETE', 'REMOVE', 'REMOVEUNUSEDRESOURCE', 'TERMINATE', 'DEALLOCATE'].includes(t))         recPillClass = 'rec-id';
  else if (['DOWNSIZE', 'RIGHTSIZE', 'DOWNGRADE'].includes(t))                                             recPillClass = 'rec-rs';
  else if (['AUTO-PAUSE', 'AUTOPAUSE', 'AUTO_PAUSE', 'PAUSE', 'SCHEDULE', 'SCHEDULESTART'].includes(t))    recPillClass = 'rec-warn';
  else if (['MOVE', 'MOVETOSERVERLESS', 'MOVE_TO_SERVERLESS', 'SERVERLESS'].includes(t))                  recPillClass = 'rec-tier';
  else if (['BUY', 'BUY_RI', 'BUY_RESERVED', 'PURCHASE_RI', 'RESERVED', 'RI', 'BUY 1-YR RI'].includes(t)) recPillClass = 'rec-buy';
  return { recType: (t === 'NA' || !t) ? 'OPTIMAL' : t, recPillClass };
};

// ── Purchasing-model detection ────────────────────────────────

const detectModel = (item, tier, sku) => {
  const pm   = String(item.purchasingModel ?? '').toUpperCase().trim();
  const kind = String(item.poolKind ?? item.kind ?? '').toLowerCase();
  const t    = String(tier ?? '').toLowerCase();
  const s    = String(sku ?? '').toLowerCase();
  if (pm === 'DTU' || kind.includes('dtu')) return 'DTU';
  if (t.includes('serverless') || s.includes('serverless'))                 return 'Serverless';
  if (t.includes('businesscritical') || t.includes('business critical') || s.includes('bc_')) return 'vCore (BC)';
  if (t.includes('hyperscale') || s.includes('hs_'))                        return 'vCore (HS)';
  if (pm === 'VCORE' || t.includes('generalpurpose') || s.includes('gp_'))  return 'vCore (GP)';
  if (['basic', 'standard', 'premium'].includes(t))                         return 'DTU';
  return 'vCore (GP)';
};

// ── Row Mapper (handles Combined / Custom / Azure schemas) ────

const toSQLRow = (item, kind = 'database') => {
  const current     = parseCost(item.currentMonthlyPriceValue ?? item.currentDiskMonthlyPriceValue);
  const recommended = parseCost(item.recommendedMonthlyPriceValue ?? 0);
  const saving      = parseCost(item.savingCost ?? item.savingsAmount ?? (current - recommended));
  const rawRec      = item.recommendationType ?? item.remark ?? (saving > 0 ? 'DOWNSIZE' : 'OPTIMAL');
  const { recType, recPillClass } = resolveSQLRec(rawRec);

  // Prefer the backend's human-readable detail, then fall back through the other
  // remark-style keys, and finally to the resolved rec type.
  const notNA = (v) => (v && String(v).trim().toUpperCase() !== 'NA') ? v : null;
  const remark =
    notNA(item.recommendationDetail) ??
    notNA(item.recommendationSubCategory) ??
    notNA(item.customRecomendationRemark) ??
    notNA(item.remark) ??
    (recType === 'OPTIMAL' ? 'Optimal' : recType);

  const num     = (v) => (v == null || v === '' ? null : Number(v));
  const cpu     = num(item.avgCpuValue ?? item.avgCpuUsage ?? item.cpuUtilization);
  const peakCpu = num(item.peakCpuValue);
  const p95Cpu  = num(item.p95CpuValue);
  const p99Cpu  = num(item.p99CpuValue);

  const sku     = item.currentSkuName ?? item.poolSkuName ?? item.currentSku ?? item.skuName ?? item.sku ?? '—';
  const tier    = item.currentTier ?? item.poolTier ?? item.skuTier ?? '—';
  const model   = detectModel(item, tier, sku);
  const compute = item.vCoreAndDtu ?? (item.maxCapacity != null ? `${item.minCapacity ?? 0}–${item.maxCapacity} vCore` : (item.currentCapacity != null ? String(item.currentCapacity) : '—'));

  // Pool payloads are pool-centric (poolName / poolLocation / poolState / serverSate typo);
  // database payloads are db-centric (dbName / location / status).
  const primaryName = kind === 'pool'
    ? (item.poolName ?? item.name ?? '—')
    : (item.dbName ?? item.name ?? item.databaseName ?? '—');

  return {
    id: item.id ?? item.databaseId ?? item.resourceId ?? '—',
    serverName: item.serverName ?? item.managedInstanceName ?? (item.dbName ? item.name : null) ?? '—',
    dbName: primaryName,
    location: item.location ?? item.resourceLocation ?? item.poolLocation ?? '—',
    resourceGroup: item.resourceGroup ?? '—',
    version: item.version ?? item.poolKind ?? item.kind ?? '—',
    sku, tier, model, compute,
    cpu, peakCpu, p95Cpu, p99Cpu,
    status: item.status ?? item.serverState ?? item.serverSate ?? item.poolState ?? item.state ?? '—',
    cost: `$${current.toFixed(2)}`,
    costValue: current,
    recommendedCost: recommended > 0 ? `$${recommended.toFixed(2)}` : '—',
    recommendedCostValue: recommended,
    recommendationRemark: remark,
    recType,
    recPillClass,
    saving: formatSavingStr(saving),
    actionLabel: 'Assist with AI',
    actionClass: 'mini-btn ai-assist',
  };
};

const mapRows = (data, kind = 'database') => (data ?? []).map((item) => toSQLRow(item, kind));

// ── KPI ──────────────────────────────────────────────────────

const buildKpiData = (totalMonthlyCost, totalCount, rows, countTitle = 'SQL Databases') => {
  const rowCurrent  = rows.reduce((s, r) => s + r.costValue, 0);
  const rowRec      = rows.reduce((s, r) => s + r.recommendedCostValue, 0);
  const current     = totalMonthlyCost != null ? parseCost(totalMonthlyCost.currentCost)     : rowCurrent;
  const recommended = totalMonthlyCost != null ? parseCost(totalMonthlyCost.recommendedCost) : rowRec;
  const saving      = totalMonthlyCost != null ? parseCost(totalMonthlyCost.savingCost)      : Math.max(0, current - recommended);
  const vCoreCount  = rows.filter((r) => r.model.startsWith('vCore')).length;
  const dtuCount    = rows.filter((r) => r.model === 'DTU').length;

  return buildBaseCostKpis({
    countTitle,
    countValue: totalCount,
    countSubtitle: `${vCoreCount} vCore · ${dtuCount} DTU`,
    current, saving, recommended,
  });
};

// ── Charts (same set as the MySQL page) ───────────────────────

const buildTrendChartData = (rows) => {
  const active = rows
    .filter((r) => (r.costValue ?? 0) > 0 || (r.cpu != null && r.cpu > 0))
    .map((r) => ({ name: r.dbName, cpu: r.cpu ?? 0, cost: (r.costValue ?? 0) / 30 }))
    .sort((a, b) => b.cost - a.cost || b.cpu - a.cpu)
    .slice(0, 10);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.name),
    datasets: [
      { label: 'Avg CPU %', data: active.map((r) => r.cpu), borderColor: '#0078d4', backgroundColor: 'rgba(0,120,212,0.15)', fill: true, tension: 0.3, yAxisID: 'y1', borderWidth: 2, pointRadius: 3 },
      { label: 'Daily Cost ($)', data: active.map((r) => parseFloat(r.cost.toFixed(2))), borderColor: '#f57c00', fill: false, tension: 0.3, yAxisID: 'y2', borderWidth: 2, pointRadius: 3, borderDash: [4, 3] },
    ],
  };
};

const buildCpuProfileChartData = (rows) => {
  const active = rows
    .filter((r) => r.cpu != null || r.peakCpu != null)
    .sort((a, b) => (b.peakCpu ?? b.cpu ?? 0) - (a.peakCpu ?? a.cpu ?? 0))
    .slice(0, 8);
  if (!active.length) return null;
  return {
    labels: active.map((r) => r.dbName),
    datasets: [
      { label: 'Avg',  data: active.map((r) => r.cpu     ?? 0), backgroundColor: '#0078d4', borderRadius: 3, barPercentage: 0.75 },
      { label: 'P95',  data: active.map((r) => r.p95Cpu  ?? 0), backgroundColor: '#26c6da', borderRadius: 3, barPercentage: 0.75 },
      { label: 'P99',  data: active.map((r) => r.p99Cpu  ?? 0), backgroundColor: '#7e57c2', borderRadius: 3, barPercentage: 0.75 },
      { label: 'Peak', data: active.map((r) => r.peakCpu ?? 0), backgroundColor: '#f57c00', borderRadius: 3, barPercentage: 0.75 },
    ],
  };
};

const buildSkuChartData = (rows) => {
  if (!rows.length) return null;
  const map = {};
  const hasCost = rows.some((r) => r.costValue > 0);
  rows.forEach((r) => { if (r.sku && r.sku !== '—') map[r.sku] = (map[r.sku] || 0) + (hasCost ? r.costValue : 1); });
  const sorted = Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.length) return null;
  return { labels: sorted.map(([c]) => c), datasets: [{ label: hasCost ? 'Cost ($)' : 'Count', data: sorted.map(([, v]) => parseFloat(v.toFixed(2))), backgroundColor: '#0078d4', borderRadius: 4, barPercentage: 0.6 }] };
};

const buildModelDonut = (rows) => {
  if (!rows.length) return null;
  const palette = ['#0078d4', '#8e24aa', '#f57c00', '#2ea66f', '#26c6da', '#ec407a'];
  const map = {};
  const hasCost = rows.some((r) => r.costValue > 0);
  rows.forEach((r) => { const m = r.model || 'Other'; map[m] = (map[m] || 0) + (hasCost ? r.costValue : 1); });
  const entries = Object.entries(map).filter(([, v]) => v > 0);
  if (!entries.length) return null;
  return {
    labels: entries.map(([m]) => m),
    datasets: [{ data: entries.map(([, v]) => parseFloat(v.toFixed(2))), backgroundColor: entries.map((_, i) => palette[i % palette.length]), borderWidth: 2, borderColor: '#fff' }],
  };
};

const buildInsightActions = (rows) => {
  const RI_RECS   = ['BUY', 'BUY_RI', 'BUY_RESERVED', 'PURCHASE_RI', 'RESERVED', 'RI', 'BUY 1-YR RI'];
  const MOVE_RECS = ['MOVE', 'MOVETOSERVERLESS', 'MOVE_TO_SERVERLESS', 'SERVERLESS'];
  const withSaving = rows
    .filter((r) => parseSaving(r.saving) > 0)
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →', actionClass = 'mini-btn';
    if (['DOWNSIZE', 'RIGHTSIZE', 'DOWNGRADE'].includes(r.recType))                        { actionLabel = 'Rightsize →'; }
    else if (['STOP', 'DELETE', 'TERMINATE', 'DEALLOCATE'].includes(r.recType))            { actionLabel = 'Stop →';      actionClass = 'mini-btn ghost'; }
    else if (MOVE_RECS.includes(r.recType))                                                { actionLabel = 'Apply →'; }
    else if (RI_RECS.includes(r.recType))                                                  { actionLabel = 'Buy RI →';    actionClass = 'mini-btn ghost'; }
    return { id: i + 1, title: r.dbName || r.id, amount: r.saving, desc: `${r.sku} · ${r.location}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Table Columns ─────────────────────────────────────────────

const makeSqlColumns = (primaryLabel = 'Database') => [
  { key: 'dbName',         label: primaryLabel },
  { key: 'serverName',     label: 'Server' },
  { key: 'sku',            label: 'SKU (Tier)', render: (row) => (row.tier && row.tier !== '—') ? `${row.sku} (${row.tier})` : row.sku },
  {
    key: 'status',         label: 'Status',
    render: (row) => {
      const isAvail = ['available', 'running', 'ready', 'active', 'enabled', 'online'].includes((row.status ?? '').toLowerCase().trim());
      return <span className={`status-pill ${isAvail ? 'st-avail' : 'st-stopped'}`}>{row.status}</span>;
    },
  },
  { key: 'cpu',            label: 'CPU Avg',           render: (row) => <UtilBar avgValue={row.cpu} peakValue={row.peakCpu} /> },
  { key: 'p99Cpu',         label: 'P99 CPU',           className: 'num', render: (row) => row.p99Cpu != null ? <span>{row.p99Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'p95Cpu',         label: 'P95 CPU',           className: 'num', render: (row) => row.p95Cpu != null ? <span>{row.p95Cpu}%</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'cost',           label: 'Cost / Mo',         className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation', render: (row) => <RecCell row={row} /> },
  { key: 'saving',         label: 'Saving / Mo',       className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost',label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
  {
    key: 'action',         label: 'Action',
    render: (row) => (
      <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>
        {row.actionLabel}
      </button>
    ),
  },
];

// ── Main Page ─────────────────────────────────────────────────

const AzureDbSQLOptimization = () => {
  const { selectedAccount } = useAuth();

  const [activeTab, setActiveTab] = useState(0);
  const [viewTab,   setViewTab]   = useState('sqlDatabase');

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom,   setIsLoadingCustom]   = useState(false);
  const [isLoadingAzure,    setIsLoadingAzure]    = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows,   setCustomRows]   = useState([]);
  const [azureRows,    setAzureRows]    = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi,   setCustomKpi]   = useState([]);
  const [azureKpi,    setAzureKpi]    = useState([]);

  const [trendData,      setTrendData]      = useState(null);
  const [modelData,      setModelData]      = useState(null);
  const [cpuProfileData, setCpuProfileData] = useState(null);
  const [skuData,        setSkuData]        = useState(null);

  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal,   setInsightTotal]   = useState(0);

  const [tagList,        setTagList]        = useState([]);
  const [filterList,     setFilterList]     = useState({ tag: '', values: [] });
  const [modelFilter,    setModelFilter]    = useState('');
  const [tierFilter,     setTierFilter]     = useState('');
  const [locationFilter, setLocationFilter] = useState('');

  const [aiOpen,   setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData,   setAiData]   = useState(null);

  const getUserId = () => {
    const raw = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
    if (!raw) return null;
    try { return JSON.parse(raw)?.userId ?? null; } catch { return null; }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      dbId: row.id, dbName: row.dbName, serverName: row.serverName, location: row.location,
      resourceGroup: row.resourceGroup, sku: row.sku, tier: row.tier, model: row.model,
      avgCpu: row.cpu, currentMonthlyCost: row.cost, recommendation: row.recType,
      recommendationDetail: row.recommendationRemark, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the Azure SQL Database data provided, give a concise, natural insight ' +
      'directly to the user — not a formal report. Mention the cost situation, savings within reach, ' +
      'and what they should consider doing. Keep it conversational, friendly, and to the point.'
    );
    setAiOpen(true);
  };

  // fetchedRef keys are `${viewKey}:${tabIndex}` so each resource-type view lazily
  // loads its own three sub-tabs independently.
  const fetchedRef = useRef(new Set());

  const fetchTagList = (viewKey) => {
    const types = VIEW_API_TYPES[viewKey];
    if (!selectedAccount || !types) { setTagList([]); return; }
    setTagList([]);
    InventoryApiService.getAzureFilterTagList(
      APIConstants.ENDPOINT_AZURE_FILTER_TAG,
      { type: types.tagType, userAccountId: selectedAccount }
    )
      .then((res) => setTagList(res?.data ?? []))
      .catch(() => {});
  };

  const fetchCombinedData = (userId, viewKey, isForceRefresh = false) => {
    const types = VIEW_API_TYPES[viewKey];
    if (!types) return;
    setIsLoadingCombined(true);
    setCombinedRows([]);
    const reqObj = { userId, type: types.combined, userAccountId: selectedAccount };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapRows(res?.data, types.kind);
        setCombinedRows(rows);
        setCombinedKpi(buildKpiData(res?.totalMonthlyCost, rows.length, rows, types.kpiTitle));
      })
      .catch((err) => console.error('Error fetching combined recommendations', err))
      .finally(() => setIsLoadingCombined(false));
  };

  const fetchCustomData = (userId, viewKey, isForceRefresh = false) => {
    const types = VIEW_API_TYPES[viewKey];
    if (!types) return;
    setIsLoadingCustom(true);
    setCustomRows([]);
    const reqObj = {
      userId, type: types.custom, userAccountId: selectedAccount,
      filterKey: filterList.tag || undefined,
      filterValues: filterList.values.length > 0 ? filterList.values : undefined,
    };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapRows(res?.data, types.kind);
        setCustomRows(rows);
        setCustomKpi(buildKpiData(res?.totalMonthlyCost, rows.length, rows, types.kpiTitle));
      })
      .catch((err) => console.error('Error fetching custom recommendations', err))
      .finally(() => setIsLoadingCustom(false));
  };

  const fetchAzureData = (userId, viewKey, isForceRefresh = false) => {
    const types = VIEW_API_TYPES[viewKey];
    if (!types) return;
    setIsLoadingAzure(true);
    setAzureRows([]);
    const reqObj = { userId, type: types.azure, userAccountId: selectedAccount };
    if (isForceRefresh) reqObj.isForceRefresh = true;
    RecommendationApiService.getAzureData(reqObj)
      .then((res) => {
        const rows = mapRows(res?.data, types.kind);
        setAzureRows(rows);
        setAzureKpi(buildKpiData(res?.totalMonthlyCost, rows.length, rows, types.kpiTitle));
      })
      .catch((err) => console.error('Error fetching azure recommendations', err))
      .finally(() => setIsLoadingAzure(false));
  };

  const fetchTabData = (tabIndex, viewKey = viewTab, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!VIEW_API_TYPES[viewKey]) return;   // API for this resource type not wired yet
    const key = `${viewKey}:${tabIndex}`;
    if (!isForceRefresh && fetchedRef.current.has(key)) return;
    fetchedRef.current.add(key);

    const userId = getUserId();
    if (!userId) return;

    if (tabIndex === 0)      fetchCombinedData(userId, viewKey, isForceRefresh);
    else if (tabIndex === 1) fetchCustomData(userId, viewKey, isForceRefresh);
    else if (tabIndex === 2) fetchAzureData(userId, viewKey, isForceRefresh);
  };

  const resetViewData = () => {
    setCombinedRows([]); setCustomRows([]); setAzureRows([]);
    setCombinedKpi([]);  setCustomKpi([]);  setAzureKpi([]);
  };

  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i, viewTab);
  };

  const handleViewChange = (key) => {
    if (key === viewTab) return;
    setViewTab(key);
    setActiveTab(0);
    setFilterList({ tag: '', values: [] });
    resetViewData();
    fetchTabData(0, key);
    fetchTagList(key);
  };

  const handleRefresh = () => {
    if (isLoadingActive) return;
    fetchedRef.current.delete(`${viewTab}:${activeTab}`);
    fetchTabData(activeTab, viewTab, true);
    fetchTagList(viewTab);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') { setFilterList({ tag: value ?? '', values: [] }); return; }
    if (field === 'values') setFilterList((prev) => ({ tag: prev.tag, values: typeof value === 'string' ? value.split(',') : (value ?? []) }));
  };

  useEffect(() => {
    if (selectedAccount) {
      setFilterList({ tag: '', values: [] });
      fetchedRef.current = new Set();
      setActiveTab(0);
      resetViewData();
      fetchTabData(0, viewTab);
      fetchTagList(viewTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  // Refetch CloudsAnalytics (custom) data only while its tab is active — the tag
  // filters are server-side. Guarding on activeTab keeps the custom API from firing
  // on mount / view-switch alongside the default Combined fetch.
  useEffect(() => {
    if (activeTab !== 1 || !selectedAccount) return;
    if (filterList.tag === '' || filterList.values.length > 0) {
      const userId = getUserId();
      if (userId) {
        fetchedRef.current.add(`${viewTab}:1`);
        fetchCustomData(userId, viewTab);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList]);

  const activeKpi       = activeTab === 0 ? combinedKpi       : activeTab === 1 ? customKpi       : azureKpi;
  const isLoadingActive = activeTab === 0 ? isLoadingCombined : activeTab === 1 ? isLoadingCustom : isLoadingAzure;
  const baseRows        = activeTab === 0 ? combinedRows      : activeTab === 1 ? customRows      : azureRows;

  const viewMeta   = VIEW_API_TYPES[viewTab] ?? {};
  const sqlColumns = useMemo(() => makeSqlColumns(viewMeta.entity ?? 'Database'), [viewMeta.entity]);

  const models    = useMemo(() => [...new Set(baseRows.map((r) => r.model).filter(Boolean))].sort(), [baseRows]);
  const tiers     = useMemo(() => [...new Set(baseRows.map((r) => r.tier).filter((v) => v && v !== '—'))].sort(), [baseRows]);
  const locations = useMemo(() => [...new Set(baseRows.map((r) => r.location).filter(Boolean))].sort(), [baseRows]);

  const filteredRows = useMemo(() => {
    let rows = baseRows;
    if (modelFilter)    rows = rows.filter((r) => r.model === modelFilter);
    if (tierFilter)     rows = rows.filter((r) => r.tier === tierFilter);
    if (locationFilter) rows = rows.filter((r) => r.location === locationFilter);
    return rows;
  }, [baseRows, modelFilter, tierFilter, locationFilter]);

  const activeRows = filteredRows.map((row) => ({ ...row, onAction: handleDetailsClick }));

  useEffect(() => {
    if (filteredRows.length > 0) {
      setTrendData(buildTrendChartData(filteredRows));
      setModelData(buildModelDonut(filteredRows));
      setCpuProfileData(buildCpuProfileChartData(filteredRows));
      setSkuData(buildSkuChartData(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setTrendData(null);
      setModelData(null);
      setCpuProfileData(null);
      setSkuData(null);
      setInsightActions([]);
      setInsightTotal(0);
    }
  }, [filteredRows]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Azure SQL Optimization',
      view: VIEW_TABS.find((t) => t.key === viewTab)?.label,
      kpi: activeKpi,
      sqlRecommendations: filteredRows,
    });
  }, [filteredRows, activeKpi, viewTab]);

  const handleExport = () =>
    exportCSV(activeRows, sqlColumns.filter((c) => c.key !== 'action'), `azure-db-sql-${viewTab}-tab${activeTab}.csv`);

  const clearLocalFilters = () => {
    setModelFilter(''); setTierFilter(''); setLocationFilter('');
    setFilterList({ tag: '', values: [] });
  };

  const viewSupported = !!VIEW_API_TYPES[viewTab];

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> Azure DB{' '}
        <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>SQL</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            Azure SQL Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{baseRows.length} {viewMeta.entityPlural ?? 'databases'}</span>}
          </h1>
          <div className="meta">Azure SQL Database · downsize vCores, stop idle databases, move low-traffic to serverless, reserve steady prod</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport}>⬇ Export</button>
          <button className="btn ghost" onClick={handleRefresh} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      <div className="tab-row">
        {VIEW_TABS.map((t) => (
          <div key={t.key} className={`tab${viewTab === t.key ? ' active' : ''}`} onClick={() => handleViewChange(t.key)}>
            {t.label}
          </div>
        ))}
      </div>

      <div className="grid-3">
        <Card className="first" title="SQL utilization & cost" subtitle="CPU utilization vs daily cost (Top 10 databases)">
          <LineChart data={trendData} loading={isLoadingActive} chartType="line" y2BeginAtZero={false} xMaxRotation={0} y1Label="" y2Label="Daily Cost ($)" />
        </Card>
        <Card className="first" title="Cost by purchasing model" subtitle="vCore (GP/BC/HS) / DTU / Serverless">
          <DonutChart data={modelData} loading={isLoadingActive} />
        </Card>
        <TopActionsCard title="Top SQL actions" actions={insightActions} total={insightTotal} loading={isLoadingActive} entityName={viewMeta.entityPlural ?? 'databases'} onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <div className="sub-tabs">
        {subTabs.map((t, i) => (
          <div key={i} className={`sub-tab${activeTab === i ? ' active' : ''}`} onClick={() => handleTabChange(i)}>
            {t.label}
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <div className="field">
          <label>Filter Tag</label>
          <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
            <option value="">None</option>
            {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Filter Tag Values</label>
          <MultiSelect
            options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
            value={filterList.values}
            onChange={(vals) => handleFilterChange('values', vals)}
            disabled={!filterList.tag}
          />
        </div>
        <div className="field">
          <label>Purchasing Model</label>
          <select value={modelFilter} onChange={(e) => setModelFilter(e.target.value)}>
            <option value="">All</option>
            {models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Service Tier</label>
          <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value)}>
            <option value="">All</option>
            {tiers.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Location</label>
          <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
            <option value="">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="spacer" />
        {(filterList.tag || modelFilter || tierFilter || locationFilter) && (
          <button className="btn ghost" onClick={clearLocalFilters}>✕ Clear</button>
        )}
        <button className="btn ghost" onClick={handleRefresh} disabled={isLoadingActive}>↻ {isLoadingActive ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      {viewSupported ? (
        <Table
          rows={activeRows}
          columns={sqlColumns}
          loading={isLoadingActive}
          skeletonRows={8}
          pageSizes={[10, 25, 50, 100]}
          defaultPageSize={10}
          defaultSortKey="saving"
          defaultSortDir="desc"
          onExport={handleExport}
          emptyText={`No ${VIEW_TABS.find((t) => t.key === viewTab)?.label ?? 'SQL'} recommendations`}
        />
      ) : (
        <div className="card middle">
          <div className="table-loading" style={{ color: 'var(--muted)' }}>
            {VIEW_TABS.find((t) => t.key === viewTab)?.label} recommendations coming soon.
          </div>
        </div>
      )}

      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '20px', marginBottom: '4px' }}>
        Utilization
      </div>
      <div className="grid-2 grid-gap-top">
        <Card title="CPU profile — Avg / P95 / P99 / Peak" subtitle={`Headroom analysis per ${(viewMeta.entity ?? 'Database').toLowerCase()}`}>
          <GroupedBarChart data={cpuProfileData} loading={isLoadingActive} />
        </Card>
        <Card title="Cost by SKU Tiers" subtitle="Top SKU families this month">
          <HorizontalBarChart data={skuData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal open={aiOpen} onClose={() => setAiOpen(false)} prompt={aiPrompt} data={aiData} />
    </>
  );
};

export default AzureDbSQLOptimization;
